/**
 * AgentContext — State management for the Stratalogy Agent chat widget.
 *
 * Provides:
 *  - isOpen / toggleOpen / closeAgent / openAgent — widget visibility
 *  - conversations — list of past conversations
 *  - activeConversation — current conversation with messages
 *  - sendMessage(content) — send a message (handles streaming)
 *  - startNewConversation() — clear active conversation
 *  - loadConversation(id) — load a past conversation
 *  - deleteConversation(id) — remove a conversation
 *  - submitFeedback(messageId, conversationId, feedback) — thumbs up/down
 *  - isStreaming — whether the agent is currently responding
 *  - toolStatus — current tool execution status text (Phase 2)
 *  - pagePath — current page route (auto-detected)
 *  - pendingActions: every write action awaiting confirmation, in order (CARDS-1)
 *  - pendingAction: the first of pendingActions, or null (send stays blocked while any waits)
 *  - confirmAction(actionId) / cancelAction(actionId): confirm or cancel one card
 *  - confirmAll() / cancelAll(): confirm or cancel the waiting cards one at a time
 *  - cardsBusy: true while confirmAll or cancelAll runs
 *  - insights — proactive insight cards for welcome screen (Phase 4)
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { createSendGuard } from "../lib/agentSendGuard";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { localToday } from "../utils/formats";
import { emitProjectChanged } from "../lib/projectEvents";
import { useAuth } from "./AuthContext";
import {
  saveConversationId,
  readConversationId,
  clearConversationId,
  removeLegacyConversationKey,
  conversationMatchesScope,
  chatErrorMessage,
  EMPTY_REPLY_CLIENT,
  DROPPED_REPLY_CLIENT,
  TURN_IN_PROGRESS_TEXT,
  createClientRequestId,
} from "../lib/agentSession";

// TURN-1: a request that fails before any reply (network error, 502, 503 or
// 504) is sent again once, after this wait. Never after a reply started.
const CHAT_RETRY_DELAY_MS = 3000;
const CHAT_RETRY_STATUSES = new Set([502, 503, 504]);
const waitMs = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const AgentContext = createContext(null);

// CARDS-1: pure helpers for the list of waiting confirm cards.
export function addPendingAction(list, action) {
  if (!action || !action.actionId) return list;
  if (list.some((a) => a.actionId === action.actionId)) return list;
  const next = { actionId: action.actionId, description: action.description };
  // DUP-2: only a possible_duplicate card carries the warning summary
  if (action.result === "possible_duplicate") {
    next.result = "possible_duplicate";
    if (action.possibleDuplicate) next.possibleDuplicate = action.possibleDuplicate;
  }
  return [...list, next];
}

export function removePendingAction(list, actionId) {
  const next = list.filter((a) => a.actionId !== actionId);
  return next.length === list.length ? list : next;
}

export const CONFIRM_ERROR_TEXT = "Failed to execute the action. Please try again.";

/** The server's message for a failed confirm (for example an expired card), else the default text. */
export function confirmErrorText(err) {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (detail && typeof detail.message === "string" && detail.message.trim()) return detail.message.trim();
  return CONFIRM_ERROR_TEXT;
}

export function useAgent() {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error("useAgent must be inside AgentProvider");
  return ctx;
}

export function AgentProvider({ children }) {
  const location = useLocation();

  // ─── State ──────────────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationTitle, setConversationTitle] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  // Unread indicator (new response while widget is closed)
  const [hasUnread, setHasUnread] = useState(false);

  // Tool execution status (Phase 2 — shows what the agent is doing during tool calls)
  const [toolStatus, setToolStatus] = useState("");

  // CARDS-1: every write action awaiting confirmation, in the order the cards came
  const [pendingActions, setPendingActions] = useState([]);
  const pendingActionsRef = useRef(pendingActions);
  pendingActionsRef.current = pendingActions;
  const pendingAction = pendingActions[0] || null;
  const [cardsBusy, setCardsBusy] = useState(false);
  const msgSeqRef = useRef(0);
  const nextMsgId = (prefix) => {
    msgSeqRef.current += 1;
    return `${prefix}-${Date.now()}-${msgSeqRef.current}`;
  };

  // Proactive insights for welcome screen (Phase 4)
  const [insights, setInsights] = useState([]);

  const abortRef = useRef(null);
  // R4 PR 2: synchronous in-flight guard (isStreaming state is stale until re-render).
  const inFlightRef = useRef(null);
  if (inFlightRef.current === null) inFlightRef.current = createSendGuard();

  // ─── Page path (auto-detected from React Router) ───────────────────────────
  const pagePath = location.pathname.replace(/^\//, "");

  // ─── Smart page context: extract project_id from URL (Phase 3) ────────────
  // Matches /project/:id or /project/:id/anything (singular — matches the React Router route)
  const projectIdMatch = location.pathname.match(/\/project\/([a-f0-9-]+)/i);
  const activeProjectId = projectIdMatch ? projectIdMatch[1] : null;
  // CHAT-HIST-1: scope is the project id from the URL, or dashboard. No signed-in
  // user keeps the single STAB-1A key so a reload still reopens that chat.
  const auth = useAuth();
  const userId = auth && auth.user && auth.user.id ? String(auth.user.id) : "";
  const authLoading = Boolean(auth && auth.loading);
  const chatScope = activeProjectId || "dashboard";

  // ─── Load conversation list ────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      // CHAT-HIST-1: a project page asks for that project. The dashboard does not.
      const response = activeProjectId
        ? await api.get("/agent/conversations", { params: { project_id: activeProjectId } })
        : await api.get("/agent/conversations");
      setConversations(response.data);
    } catch (err) {
      // Silently fail — conversations list is non-critical
      console.warn("Failed to load agent conversations:", err);
    }
  }, [activeProjectId]);

  // Load conversations and insights when widget opens
  useEffect(() => {
    if (isOpen) {
      loadConversations();
      // Phase 4: fetch proactive insights (T4: scoped to current project when on a project page)
      (async () => {
        try {
          const params = activeProjectId ? { project_id: activeProjectId } : {};
          const { data } = await api.get("/agent/insights", { params });
          setInsights(data.insights || []);
        } catch (err) {
          console.warn("Failed to load agent insights:", err);
        }
      })();
    }
  }, [isOpen, loadConversations, activeProjectId]);

  // DASH-1: project chosen from the dashboard chat, for this conversation only.
  // Not stored in localStorage. A project page still wins over this pick.
  const [dashboardProject, setDashboardProjectState] = useState(null);

  const setDashboardProject = useCallback((project) => {
    if (!project || !project.id) {
      setDashboardProjectState(null);
      return;
    }
    setDashboardProjectState({ id: project.id, name: project.name || "Untitled" });
  }, []);

  // ─── Load a specific conversation ──────────────────────────────────────────
  // STAB-1A: returns true when the conversation loaded, false otherwise
  const loadConversation = useCallback(async (conversationId, options) => {
    try {
      const { data } = await api.get(`/agent/conversations/${conversationId}`);
      // CHAT-HIST-1: the restore passes allow() so a saved id from another scope stays closed.
      if (options && typeof options.allow === "function" && !options.allow(data)) return false;
      setActiveConversationId(conversationId);
      setMessages(data.messages || []);
      setConversationTitle(data.title || "");
      setShowHistory(false);
      setPendingActions([]);
      setDashboardProjectState(null);
      return true;
    } catch (err) {
      console.error("Failed to load conversation:", err);
      return false;
    }
  }, []);

  // STAB-1A: keep the open conversation id for a reload (an id only, never text).
  // CHAT-HIST-1: write only the key for the current user and page. A scope
  // change must not copy the open id onto the next page's key.
  const savedForRef = useRef({ userId, chatScope });
  useEffect(() => {
    // DEMO-1: never clear here; this runs on the first mount before the restore
    if (!activeConversationId) {
      savedForRef.current = { userId, chatScope };
      return;
    }
    if (savedForRef.current.userId !== userId || savedForRef.current.chatScope !== chatScope) return;
    const scopeRef = userId ? { userId, scope: chatScope } : null;
    saveConversationId(activeConversationId, scopeRef);
  }, [activeConversationId, userId, chatScope]);

  // CHAT-HIST-1: once a user is known, drop the old global key. Do not migrate it.
  useEffect(() => {
    if (userId) removeLegacyConversationKey();
  }, [userId]);

  // STAB-1A: reopen the last conversation when the panel opens empty.
  // A page change does not wipe the open chat (DASH-1's pick stays in state).
  // A different signed-in user must not see the previous user's messages.
  const restoringRef = useRef(false);
  const seenUserRef = useRef(userId);
  useEffect(() => {
    if (seenUserRef.current === userId) return;
    seenUserRef.current = userId;
    restoringRef.current = false;
    setActiveConversationId(null);
    setMessages([]);
    setConversationTitle("");
    setShowHistory(false);
    setPendingActions([]);
    setDashboardProjectState(null);
  }, [userId]);

  useEffect(() => {
    if (authLoading) return;
    if (!isOpen || activeConversationId || messages.length > 0 || restoringRef.current) return;
    const scopeRef = userId ? { userId, scope: chatScope } : null;
    const savedId = readConversationId(scopeRef);
    if (!savedId) return;
    restoringRef.current = true;
    (async () => {
      const ok = await loadConversation(savedId, {
        allow: (data) => !userId || conversationMatchesScope(data, chatScope),
      });
      if (!ok) clearConversationId(scopeRef);
      restoringRef.current = false;
    })();
  }, [isOpen, activeConversationId, messages.length, loadConversation, userId, chatScope, authLoading]);

  // ─── Start new conversation ────────────────────────────────────────────────
  const startNewConversation = useCallback(() => {
    clearConversationId(userId ? { userId, scope: chatScope } : null);
    setActiveConversationId(null);
    setMessages([]);
    setConversationTitle("");
    setShowHistory(false);
    setPendingActions([]);
    setDashboardProjectState(null);
  }, [userId, chatScope]);

  // ─── Delete conversation ───────────────────────────────────────────────────
  const deleteConversation = useCallback(async (conversationId) => {
    try {
      await api.delete(`/agent/conversations/${conversationId}`);
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (activeConversationId === conversationId) {
        startNewConversation();
      }
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  }, [activeConversationId, startNewConversation]);

  // ─── Send message (streaming) ──────────────────────────────────────────────
  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isStreaming) return;
    const guardToken = inFlightRef.current.tryStart();
    if (guardToken === null) return; // a reply is already in flight
    setPendingActions([]);

    // Add user message to local state immediately
    const userMsg = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: content.trim(),
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Add placeholder for assistant response
    const assistantPlaceholder = {
      id: `streaming-${Date.now()}`,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, assistantPlaceholder]);
    setIsStreaming(true);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("st_token");

      const requestBody = JSON.stringify({
        content: content.trim(),
        conversation_id: activeConversationId || null,
        page_path: pagePath,
        project_id: activeProjectId || (dashboardProject && dashboardProject.id) || null,
        client_today: localToday(),
        client_request_id: createClientRequestId(),
      });
      const postChat = () => fetch(`${backendUrl}/api/agent/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: requestBody,
        signal: controller.signal,
      });

      // TURN-1: one retry, with the same request, when it failed before any reply
      let response;
      for (let attempt = 0; ; attempt += 1) {
        let retry = false;
        try {
          response = await postChat();
          retry = !response.ok && CHAT_RETRY_STATUSES.has(response.status);
        } catch (fetchErr) {
          if (!(fetchErr && fetchErr.name === "TypeError") || attempt > 0) throw fetchErr;
          retry = true;
        }
        if (!retry || attempt > 0) break;
        await waitMs(CHAT_RETRY_DELAY_MS);
        if (controller.signal.aborted) return;
      }

      if (!response.ok) {
        const httpErr = new Error(`HTTP ${response.status}`);
        httpErr.status = response.status;
        throw httpErr;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamedContent = "";
      let finalMessageId = null;
      let sawError = false;
      // TURN-1: a stream that breaks is never retried; the conversation is reloaded
      let dropped = false;
      let streamConversationId = activeConversationId || null;

      while (true) {
        let chunk;
        try {
          chunk = await reader.read();
        } catch (readErr) {
          if (readErr && readErr.name === "AbortError") throw readErr;
          dropped = true;
          break;
        }
        const { done, value } = chunk;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (!payload) continue;

          try {
            const event = JSON.parse(payload);

            switch (event.type) {
              case "conversation_id":
                streamConversationId = event.content;
                setActiveConversationId(event.content);
                break;

              case "status":
                // Tool execution status (Phase 2)
                setToolStatus(event.content || "");
                break;

              case "delta":
                // Clear tool status once we start getting content
                setToolStatus("");
                streamedContent += event.content;
                setMessages((prev) => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      content: streamedContent,
                    };
                  }
                  return updated;
                });
                break;

              case "title":
                setConversationTitle(event.content);
                break;

              case "confirm":
                // Phase 3 / CARDS-1: every card in the turn is kept, in order
                setPendingActions((prev) => addPendingAction(prev, {
                  actionId: event.action_id,
                  description: event.description,
                  result: event.result,
                  possibleDuplicate: event.possible_duplicate,
                }));
                break;

              case "done":
                finalMessageId = event.message_id;
                break;

              case "error":
                sawError = true;
                setMessages((prev) => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      content: event.content || "Something went wrong. Please try again.",
                      isStreaming: false,
                      isError: true,
                    };
                  }
                  return updated;
                });
                break;

              default:
                break;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      // TURN-2: a still-running turn is not a new send. Reload once, like a drop.
      if (streamedContent === TURN_IN_PROGRESS_TEXT) {
        dropped = true;
      }

      if (dropped) {
        // TURN-1: the server keeps the partial reply (INTERRUPTED_NOTE); show what
        // it saved. Cards already shown stay. No retry: a write may have run.
        let reloaded = null;
        if (streamConversationId) {
          try {
            const { data } = await api.get(`/agent/conversations/${streamConversationId}`);
            reloaded = data && Array.isArray(data.messages) ? data : null;
          } catch {
            reloaded = null;
          }
        }
        const droppedBubble = {
          id: `dropped-${Date.now()}`,
          role: "assistant",
          content: DROPPED_REPLY_CLIENT,
          created_at: new Date().toISOString(),
          isError: true,
        };
        if (reloaded) {
          const saved = reloaded.messages;
          let lastUser = -1;
          saved.forEach((m, i) => { if (m.role === "user") lastUser = i; });
          const answered = saved.slice(lastUser + 1)
            .some((m) => m.role === "assistant" && String(m.content || "").trim());
          if (reloaded.title) setConversationTitle(reloaded.title);
          setMessages(answered ? saved : [...saved, droppedBubble]);
        } else {
          setMessages((prev) => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
              updated[lastIdx] = {
                ...updated[lastIdx],
                content: streamedContent ? `${streamedContent}\n\n${DROPPED_REPLY_CLIENT}` : DROPPED_REPLY_CLIENT,
                isStreaming: false,
                isError: true,
              };
            }
            return updated;
          });
        }
        setToolStatus("");
        loadConversations();
        return;
      }

      // Finalize the streaming message
      // STAB-1A: a stream with no text and no error never leaves an empty bubble
      const noReply = !streamedContent && !sawError;
      setMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
          updated[lastIdx] = {
            ...updated[lastIdx],
            id: finalMessageId || updated[lastIdx].id,
            isStreaming: false,
            ...(noReply ? { content: EMPTY_REPLY_CLIENT, isError: true } : {}),
          };
        }
        return updated;
      });

      // Clear tool status on completion
      setToolStatus("");

      // Set unread if widget is closed
      if (!isOpen) {
        setHasUnread(true);
      }

      // Refresh conversation list
      loadConversations();
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Agent chat error:", err);

      // Update the streaming message to show error
      setMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
          updated[lastIdx] = {
            ...updated[lastIdx],
            content: chatErrorMessage(err && err.status),
            isStreaming: false,
            isError: true,
          };
        }
        return updated;
      });
    } finally {
      inFlightRef.current.finish(guardToken);
      setIsStreaming(false);
      setToolStatus("");
      abortRef.current = null;
    }
  }, [activeConversationId, isStreaming, isOpen, pagePath, activeProjectId, dashboardProject, loadConversations]);

  // ─── Cancel streaming ──────────────────────────────────────────────────────
  const cancelStreaming = useCallback(() => {
    inFlightRef.current.cancel();
    setPendingActions([]);
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setIsStreaming(false);
    }
  }, []);

  // ─── Confirm / Cancel write actions (Phase 3, CARDS-1: one card at a time) ─
  /** Confirm one card. Resolves true when it was confirmed, false on an error.
   *  DUP-2: choice is "add_anyway" or "use_existing" when the card warned. */
  const confirmAction = useCallback(async (actionId, choice) => {
    try {
      const body = { action_id: actionId, confirm: true };
      if (choice) body.choice = choice;
      const { data } = await api.post("/agent/confirm-action", body);
      // A warning leaves the card up so the user can choose.
      if (data && data.result === "possible_duplicate") {
        setPendingActions((prev) => prev.map((item) => (
          item.actionId === actionId
            ? {
              ...item,
              result: "possible_duplicate",
              possibleDuplicate: data.existing || item.possibleDuplicate,
            }
            : item
        )));
        return false;
      }
      setPendingActions((prev) => removePendingAction(prev, actionId));
      // BILL-1: a real change refreshes the open project page and its tabs
      if (data && data.changed === true) {
        emitProjectChanged({ projectId: data.project_id, tool: data.tool });
      }
      // Use existing shows the current record. A saved card shows the server message.
      if (data && data.result === "use_existing" && data.existing) {
        const existing = data.existing;
        setMessages((prev) => [
          ...prev,
          {
            id: nextMsgId("action"),
            role: "assistant",
            content: `Possible duplicate: ${existing.title || "Untitled"}, created ${existing.created_at || ""}, ${existing.status || ""}.`,
            created_at: new Date().toISOString(),
          },
        ]);
      } else if (data && data.message) {
        setMessages((prev) => [
          ...prev,
          {
            id: nextMsgId("action"),
            role: "assistant",
            content: `\u2705 ${data.message}`,
            created_at: new Date().toISOString(),
          },
        ]);
      }
      return true;
    } catch (err) {
      console.error("Failed to confirm action:", err);
      setPendingActions((prev) => removePendingAction(prev, actionId));
      setMessages((prev) => [
        ...prev,
        {
          id: nextMsgId("action-err"),
          role: "assistant",
          content: confirmErrorText(err),
          created_at: new Date().toISOString(),
          isError: true,
        },
      ]);
      return false;
    }
  }, []);

  const cancelAction = useCallback(async (actionId) => {
    try {
      await api.post("/agent/confirm-action", {
        action_id: actionId,
        confirm: false,
      });
    } catch (err) {
      console.warn("Failed to cancel action:", err);
    }
    setPendingActions((prev) => removePendingAction(prev, actionId));
    setMessages((prev) => [
      ...prev,
      {
        id: nextMsgId("action-cancel"),
        role: "assistant",
        content: "Action cancelled.",
        created_at: new Date().toISOString(),
      },
    ]);
  }, []);

  // CARDS-1: confirm the waiting cards one at a time, in order; stop at the first error.
  const confirmAll = useCallback(async () => {
    const ids = pendingActionsRef.current.map((a) => a.actionId);
    if (!ids.length) return;
    setCardsBusy(true);
    try {
      for (const id of ids) {
        const ok = await confirmAction(id);
        if (!ok) break;
      }
    } finally {
      setCardsBusy(false);
    }
  }, [confirmAction]);

  const cancelAll = useCallback(async () => {
    const ids = pendingActionsRef.current.map((a) => a.actionId);
    if (!ids.length) return;
    setCardsBusy(true);
    try {
      for (const id of ids) {
        await cancelAction(id);
      }
    } finally {
      setCardsBusy(false);
    }
  }, [cancelAction]);

  // ─── Submit feedback ───────────────────────────────────────────────────────
  const submitFeedback = useCallback(async (messageId, conversationId, feedback) => {
    try {
      await api.post("/agent/feedback", {
        message_id: messageId,
        conversation_id: conversationId,
        feedback,
      });
    } catch (err) {
      console.warn("Failed to submit feedback:", err);
    }
  }, []);

  // ─── Toggle / open / close ─────────────────────────────────────────────────
  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
    setHasUnread(false);
  }, []);

  const openAgent = useCallback(() => {
    setIsOpen(true);
    setHasUnread(false);
  }, []);

  const closeAgent = useCallback(() => {
    setIsOpen(false);
  }, []);

  // ─── Context value ─────────────────────────────────────────────────────────
  const value = {
    isOpen,
    toggleOpen,
    openAgent,
    closeAgent,
    conversations,
    activeConversationId,
    messages,
    conversationTitle,
    isStreaming,
    toolStatus,
    hasUnread,
    showHistory,
    setShowHistory,
    pagePath,
    sendMessage,
    cancelStreaming,
    startNewConversation,
    loadConversation,
    deleteConversation,
    submitFeedback,
    loadConversations,
    // Phase 3 / CARDS-1: Write action confirmation
    pendingAction,
    pendingActions,
    confirmAction,
    cancelAction,
    confirmAll,
    cancelAll,
    cardsBusy,
    // Phase 4: Proactive insights
    insights,
    // DOC-4: chat attachments go to this project's Documents
    activeProjectId,
    // DASH-1: dashboard pick for this conversation, cleared on a new one
    dashboardProject,
    setDashboardProject,
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
}
